/**
 * Pure helpers of the stock screens.
 */
import { sortByKey } from '@/shared/lib/sort';
import type { ReservedStock, Stock } from './api';

const pad = (value: number) => String(value).padStart(2, '0');

/** `YYYY-MM-DD HH:mm:ss`, local time; 'N/A' when missing. */
export const formatStockDate = (
  value: string | Date | null | undefined,
): string => {
  if (!value) return 'N/A';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Invalid date';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};
export const QUICK_SEARCH_DELAY_MS = 200;

export type CentralStock = Stock & { calculatedQuantity: number };

/** Quantity minus the reserved stocks (orders) carrying the same EAN and SKU. */
export const withCalculatedQuantity = (
  stocks: readonly Stock[],
  reserved: readonly ReservedStock[],
): CentralStock[] =>
  stocks.map((stock) => {
    const reservedCount = reserved.filter(
      (r) =>
        r.ean13CentralFlow === stock.ean && r.m3SkuCentralFlow === stock.sku,
    ).length;
    // Subtracted from the raw value: a missing quantity gives NaN, `null` counts as 0 (see migration.md).
    const quantity =
      stock.quantity === null ? 0 : (stock.quantity ?? Number.NaN);
    return { ...stock, calculatedQuantity: quantity - reservedCount };
  });

export const CENTRAL_SORT_FIELDS = [
  'ean',
  'sku',
  'quantity',
  'calculatedQuantity',
  'lastImportId',
] as const;

export type CentralSortField = (typeof CENTRAL_SORT_FIELDS)[number];

const NUMERIC_FIELDS: readonly string[] = ['quantity', 'calculatedQuantity'];

const rawValue = (item: CentralStock, field: CentralSortField): unknown =>
  (item as Record<string, unknown>)[field];

/** Number for the quantities (NaN when missing), lower-cased text otherwise. */
const sortKey = (item: CentralStock, field: CentralSortField) =>
  NUMERIC_FIELDS.includes(field)
    ? Number(rawValue(item, field) ?? Number.NaN)
    : String(rawValue(item, field) ?? '').toLowerCase();

/** Stable sort by one field (numbers for the quantities, lower-cased text otherwise), missing numbers last. */
export const sortCentralStocks = (
  items: readonly CentralStock[],
  field: CentralSortField,
  reverse: boolean,
): CentralStock[] => sortByKey(items, (item) => sortKey(item, field), reverse);
/** Every quick-search text must be contained in its column. */
export const CENTRAL_FILTER_FIELDS = [
  'ean',
  'sku',
  'skuStock',
  'quantity',
  'calculatedQuantity',
] as const;

export type CentralSearch = Partial<
  Record<(typeof CENTRAL_FILTER_FIELDS)[number], string>
>;

export const filterCentralStocks = (
  items: readonly CentralStock[],
  search: CentralSearch,
): CentralStock[] => {
  if (!Object.values(search).some((text) => !!text)) return [...items];
  return items.filter((item) =>
    CENTRAL_FILTER_FIELDS.every((field) => {
      const text = search[field];
      if (!text) return true;
      const value = (item as Record<string, unknown>)[field];
      return String(value || '')
        .toLowerCase()
        .includes(text.toLowerCase());
    }),
  );
};

/** Sort fields of the paginated store stocks, as the API accepts them. */
export const STORE_SORT_FIELDS = [
  'storeName',
  'storeCode',
  'ean',
  'sku',
  'quantity',
  'lastImportId',
] as const;

export type StoreSortField = (typeof STORE_SORT_FIELDS)[number];

export interface StoreSort {
  by: StoreSortField;
  /** True is ascending. */
  ascending: boolean;
}

/** The field changes and the order flips, whatever the field was. */
export const nextStoreSort = (
  current: StoreSort,
  by: StoreSortField,
): StoreSort => ({ by, ascending: !current.ascending });

/** Same field flips the order, a new field sorts ascending. */
export const nextCentralSort = (
  current: { by: CentralSortField; reverse: boolean },
  by: CentralSortField,
): { by: CentralSortField; reverse: boolean } =>
  current.by === by
    ? { by, reverse: !current.reverse }
    : { by, reverse: false };
